import { useState } from "react";
import { Layout } from "@/components/layout/Layout";
import { useAuth } from "@/lib/authContext";
import { fetchWithAuth } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  Plus,
  Search,
  Calendar,
  Building2,
  RefreshCw,
  History,
  Edit2,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Clock,
  User,
  LayoutGrid,
  List,
  ExternalLink,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import { Link } from "wouter";
import { format } from "date-fns";

interface Account {
  id: string;
  account_name: string;
}

interface UserProfile {
  id: string;
  name: string;
  email: string;
}

interface FinancialSource {
  id: string;
  account_id: string;
  source_type: string;
  institution_name: string;
  account_nickname: string | null;
  external_hint: string | null;
  expected_frequency: string;
  assigned_user_id: string | null;
  last_pulled_date: string | null;
  through_date: string | null;
  status: string;
  notes: string | null;
  statement_day: number | null;
  login_url: string | null;
  accounts?: { account_name: string };
  users?: { name: string };
}

interface PullLogEntry {
  id: string;
  pull_date: string;
  through_date: string | null;
  note: string | null;
  pulled_by: string;
  users?: { name: string };
}

const SOURCE_TYPES = [
  { value: "bank", label: "Bank Account" },
  { value: "credit_card", label: "Credit Card" },
  { value: "investment", label: "Investment Account" },
  { value: "loan", label: "Loan / Liability" },
  { value: "payroll", label: "Payroll" },
  { value: "other", label: "Other" },
];

const FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive"; icon: React.ComponentType<{ className?: string }> }> = {
  ok: { label: "OK", variant: "default", icon: CheckCircle2 },
  attention: { label: "Attention", variant: "secondary", icon: Clock },
  overdue: { label: "Overdue", variant: "destructive", icon: AlertCircle },
};

export default function FinancialSources() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [filterAccount, setFilterAccount] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterFrequency, setFilterFrequency] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAssigned, setFilterAssigned] = useState("");
  const [filterLastPullBefore, setFilterLastPullBefore] = useState("");
  const [filterThroughBefore, setFilterThroughBefore] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "calendar">("table");
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set());
  const [sortCol, setSortCol] = useState<string>("account");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [calFilterPulled, setCalFilterPulled] = useState<"all" | "not_pulled" | "pulled">("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMarkPulledModal, setShowMarkPulledModal] = useState(false);
  const [showHistorySheet, setShowHistorySheet] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FinancialSource | null>(null);
  const [selectedSource, setSelectedSource] = useState<FinancialSource | null>(null);

  const [formData, setFormData] = useState({
    source_type: "bank",
    institution_name: "",
    account_nickname: "",
    external_hint: "",
    expected_frequency: "monthly",
    assigned_user_id: "",
    notes: "",
    statement_day: "" as string | number,
    login_url: "",
  });

  const [markPulledData, setMarkPulledData] = useState({
    pull_date: format(new Date(), "yyyy-MM-dd"),
    through_date: "",
    note: "",
  });

  const { data: accounts = [] } = useQuery<Account[]>({
    queryKey: ["accounts"],
    queryFn: () => fetchWithAuth<Account[]>("/api/accounts"),
  });

  const { data: users = [] } = useQuery<UserProfile[]>({
    queryKey: ["users-list"],
    queryFn: () => fetchWithAuth<UserProfile[]>("/api/users").catch(() => []),
  });

  const { data: sources = [], isLoading: sourcesLoading } = useQuery<FinancialSource[]>({
    queryKey: ["financial-sources", filterAccount],
    queryFn: () => {
      const url = filterAccount && filterAccount !== "all"
        ? `/api/financial-sources?accountId=${filterAccount}`
        : "/api/financial-sources";
      return fetchWithAuth<FinancialSource[]>(url);
    },
  });

  const { data: pullLog = [] } = useQuery<PullLogEntry[]>({
    queryKey: ["pull-log", selectedSource?.id],
    enabled: !!selectedSource && showHistorySheet,
    queryFn: () => fetchWithAuth<PullLogEntry[]>(`/api/financial-sources/${selectedSource!.id}/pull-log`),
  });

  const [addModalAccountId, setAddModalAccountId] = useState<string>("");

  const createMutation = useMutation({
    mutationFn: (data: typeof formData & { account_id: string }) =>
      fetchWithAuth<FinancialSource>("/api/financial-sources", { method: "POST", body: JSON.stringify(data) }),
    onSuccess: (_data: any, variables: any) => {
      setFilterAccount(variables.account_id);
      queryClient.invalidateQueries({ queryKey: ["financial-sources", variables.account_id] });
      setShowAddModal(false);
      resetForm();
      setAddModalAccountId("");
      toast({ title: "Source created successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<FinancialSource>) =>
      fetchWithAuth<FinancialSource>(`/api/financial-sources/${selectedSource!.id}`, { method: "PATCH", body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-sources"] });
      setShowEditModal(false);
      setSelectedSource(null);
      toast({ title: "Source updated successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const markPulledMutation = useMutation({
    mutationFn: (data: typeof markPulledData) =>
      fetchWithAuth<FinancialSource>(`/api/financial-sources/${selectedSource!.id}/mark-pulled`, { method: "POST", body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-sources"] });
      queryClient.invalidateQueries({ queryKey: ["pull-log", selectedSource?.id] });
      setShowMarkPulledModal(false);
      setSelectedSource(null);
      setMarkPulledData({ pull_date: format(new Date(), "yyyy-MM-dd"), through_date: "", note: "" });
      toast({ title: "Source marked as pulled" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const statusToggleMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      fetchWithAuth<FinancialSource>(`/api/financial-sources/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["financial-sources"] }),
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      fetchWithAuth<void>(`/api/financial-sources/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financial-sources"] });
      setDeleteTarget(null);
      toast({ title: "Source deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const resetForm = () => {
    setFormData({
      source_type: "bank",
      institution_name: "",
      account_nickname: "",
      external_hint: "",
      expected_frequency: "monthly",
      assigned_user_id: "",
      notes: "",
      statement_day: "",
      login_url: "",
    });
  };

  const handleOpenAdd = () => {
    resetForm();
    setAddModalAccountId(filterAccount !== "all" ? filterAccount : "");
    setShowAddModal(true);
  };

  const handleOpenEdit = (source: FinancialSource) => {
    setSelectedSource(source);
    setFormData({
      source_type: source.source_type,
      institution_name: source.institution_name,
      account_nickname: source.account_nickname || "",
      external_hint: source.external_hint || "",
      expected_frequency: source.expected_frequency,
      assigned_user_id: source.assigned_user_id || "",
      notes: source.notes || "",
      statement_day: source.statement_day ?? "",
      login_url: source.login_url || "",
    });
    setShowEditModal(true);
  };

  const handleOpenMarkPulled = (source: FinancialSource) => {
    setSelectedSource(source);
    setMarkPulledData({
      pull_date: format(new Date(), "yyyy-MM-dd"),
      through_date: source.through_date || "",
      note: "",
    });
    setShowMarkPulledModal(true);
  };

  const handleOpenHistory = (source: FinancialSource) => {
    setSelectedSource(source);
    setShowHistorySheet(true);
  };

  const handleCreate = () => {
    if (!addModalAccountId) {
      toast({ title: "Please select an account", variant: "destructive" });
      return;
    }
    if (!formData.institution_name.trim()) {
      toast({ title: "Institution name is required", variant: "destructive" });
      return;
    }
    createMutation.mutate({ ...formData, account_id: addModalAccountId });
  };

  const handleUpdate = () => {
    if (!formData.institution_name.trim()) {
      toast({ title: "Institution name is required", variant: "destructive" });
      return;
    }
    updateMutation.mutate({
      source_type: formData.source_type,
      institution_name: formData.institution_name,
      account_nickname: formData.account_nickname || null,
      external_hint: formData.external_hint || null,
      expected_frequency: formData.expected_frequency,
      assigned_user_id: formData.assigned_user_id && formData.assigned_user_id !== "unassigned" ? formData.assigned_user_id : null,
      notes: formData.notes || null,
      statement_day: formData.statement_day !== "" ? Number(formData.statement_day) : null,
      login_url: formData.login_url || null,
    });
  };

  const handleMarkPulled = () => {
    if (!markPulledData.pull_date) {
      toast({ title: "Pull date is required", variant: "destructive" });
      return;
    }
    markPulledMutation.mutate(markPulledData);
  };

  const hasActiveFilters = !!(searchTerm || filterType || filterFrequency || filterStatus || filterAssigned || filterLastPullBefore || filterThroughBefore);

  const clearFilters = () => {
    setSearchTerm("");
    setFilterType("");
    setFilterFrequency("");
    setFilterStatus("");
    setFilterAssigned("");
    setFilterLastPullBefore("");
    setFilterThroughBefore("");
  };

  const filteredSources = sources.filter((source) => {
    if (filterType && source.source_type !== filterType) return false;
    if (filterFrequency && source.expected_frequency !== filterFrequency) return false;
    if (filterStatus && source.status !== filterStatus) return false;
    if (filterAssigned) {
      if (filterAssigned === "__unassigned__" && source.assigned_user_id) return false;
      if (filterAssigned !== "__unassigned__" && source.assigned_user_id !== filterAssigned) return false;
    }
    if (filterLastPullBefore) {
      if (!source.last_pulled_date) return false;
      if (source.last_pulled_date > filterLastPullBefore) return false;
    }
    if (filterThroughBefore) {
      if (!source.through_date) return false;
      if (source.through_date > filterThroughBefore) return false;
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      const match =
        (source.institution_name ?? "").toLowerCase().includes(s) ||
        (source.account_nickname?.toLowerCase().includes(s)) ||
        (source.accounts?.account_name?.toLowerCase().includes(s)) ||
        (SOURCE_TYPES.find((t) => t.value === source.source_type)?.label.toLowerCase().includes(s)) ||
        (FREQUENCIES.find((f) => f.value === source.expected_frequency)?.label.toLowerCase().includes(s));
      if (!match) return false;
    }
    return true;
  });

  const sortedSources = [...filteredSources].sort((a, b) => {
    let aVal: string | number = "";
    let bVal: string | number = "";
    switch (sortCol) {
      case "account": aVal = a.accounts?.account_name ?? ""; bVal = b.accounts?.account_name ?? ""; break;
      case "institution": aVal = a.institution_name ?? ""; bVal = b.institution_name ?? ""; break;
      case "type": aVal = a.source_type; bVal = b.source_type; break;
      case "frequency": aVal = a.expected_frequency; bVal = b.expected_frequency; break;
      case "status": aVal = a.status; bVal = b.status; break;
      case "last_pulled": aVal = a.last_pulled_date ?? ""; bVal = b.last_pulled_date ?? ""; break;
      case "through_date": aVal = a.through_date ?? ""; bVal = b.through_date ?? ""; break;
      case "assigned": aVal = a.users?.name ?? ""; bVal = b.users?.name ?? ""; break;
      case "statement_day": aVal = a.statement_day ?? 0; bVal = b.statement_day ?? 0; break;
    }
    const cmp = typeof aVal === "number" ? aVal - (bVal as number) : (aVal as string).localeCompare(bVal as string);
    return sortDir === "asc" ? cmp : -cmp;
  });

  function SortHeader({ col, label }: { col: string; label: string }) {
    const active = sortCol === col;
    const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <button
        className="flex items-center gap-1 hover:text-foreground transition-colors"
        onClick={() => {
          if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
          else { setSortCol(col); setSortDir("asc"); }
        }}
      >
        {label} <Icon className="w-3 h-3 opacity-60" />
      </button>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold tracking-tight">Financial Sources</h1>
            <p className="text-muted-foreground">Track and manage recurring financial data sources.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-md border">
              <button
                onClick={() => setViewMode("table")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-l-md transition-colors ${viewMode === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                <List className="w-3.5 h-3.5" /> Table
              </button>
              <button
                onClick={() => setViewMode("calendar")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-r-md transition-colors ${viewMode === "calendar" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Calendar
              </button>
            </div>
            <Button onClick={handleOpenAdd} data-testid="button-add-source">
              <Plus className="w-4 h-4 mr-2" />
              Add Source
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative min-w-[180px] flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search institution, nickname…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-8 pl-8 text-sm"
                  data-testid="input-search-sources"
                />
              </div>
              <Select value={filterAccount || "all"} onValueChange={(v) => setFilterAccount(v === "all" ? "" : v)}>
                <SelectTrigger className="h-8 text-sm w-[180px]" data-testid="select-account-filter">
                  <SelectValue placeholder="All Accounts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Accounts</SelectItem>
                  {accounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>{acc.account_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
                <SelectTrigger className="h-8 text-sm w-[150px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {SOURCE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterFrequency || "all"} onValueChange={(v) => setFilterFrequency(v === "all" ? "" : v)}>
                <SelectTrigger className="h-8 text-sm w-[140px]">
                  <SelectValue placeholder="Frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Frequency</SelectItem>
                  {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger className="h-8 text-sm w-[130px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Status</SelectItem>
                  <SelectItem value="ok">OK</SelectItem>
                  <SelectItem value="attention">Attention</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterAssigned || "all"} onValueChange={(v) => setFilterAssigned(v === "all" ? "" : v)}>
                <SelectTrigger className="h-8 text-sm w-[150px]">
                  <SelectValue placeholder="Assigned To" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Anyone</SelectItem>
                  <SelectItem value="__unassigned__">Unassigned</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground whitespace-nowrap">Last pull ≤</span>
                <Input
                  type="date"
                  value={filterLastPullBefore}
                  onChange={(e) => setFilterLastPullBefore(e.target.value)}
                  className="h-8 text-sm w-[135px]"
                />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground whitespace-nowrap">Through ≤</span>
                <Input
                  type="date"
                  value={filterThroughBefore}
                  onChange={(e) => setFilterThroughBefore(e.target.value)}
                  className="h-8 text-sm w-[135px]"
                />
              </div>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground" onClick={clearFilters}>
                  × Clear filters
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {viewMode === "calendar" ? (
              (() => {
                if (sourcesLoading) return (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                );

                const today = new Date();
                function isPulledThisMonth(s: FinancialSource): boolean {
                  if (!s.last_pulled_date) return false;
                  const pulled = new Date(s.last_pulled_date + "T12:00:00");
                  return pulled.getMonth() === today.getMonth() && pulled.getFullYear() === today.getFullYear();
                }

                function toggleDay(day: number) {
                  setExpandedDays(prev => {
                    const next = new Set(prev);
                    next.has(day) ? next.delete(day) : next.add(day);
                    return next;
                  });
                }

                let calSources = filteredSources;
                if (calFilterPulled === "not_pulled") calSources = filteredSources.filter(s => !isPulledThisMonth(s));
                if (calFilterPulled === "pulled") calSources = filteredSources.filter(s => isPulledThisMonth(s));

                const sourcesWithDay = calSources.filter(s => s.statement_day);
                const sourcesNoDay = calSources.filter(s => !s.statement_day);
                const byDay = new Map<number, FinancialSource[]>();
                for (let d = 1; d <= 31; d++) byDay.set(d, []);
                for (const s of sourcesWithDay) byDay.get(s.statement_day!)?.push(s);
                const days1to28 = Array.from({ length: 28 }, (_, i) => i + 1);
                const lateMonth = [29, 30, 31].flatMap(d => byDay.get(d) ?? []);

                return (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-muted-foreground">Show:</span>
                      {(["all", "not_pulled", "pulled"] as const).map(v => (
                        <button
                          key={v}
                          onClick={() => setCalFilterPulled(v)}
                          className={`text-xs px-2.5 py-0.5 rounded-full border transition-colors ${calFilterPulled === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
                        >
                          {v === "all" ? "All" : v === "not_pulled" ? "Not Pulled This Month" : "Pulled This Month"}
                        </button>
                      ))}
                      <span className="text-xs text-muted-foreground ml-2">
                        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> pulled</span>
                        <span className="inline-flex items-center gap-1 ml-2"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> not yet pulled</span>
                      </span>
                    </div>

                    {sourcesWithDay.length === 0 && sourcesNoDay.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No financial sources found.</p>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-7 gap-1.5">
                          {days1to28.map(day => {
                            const daySources = byDay.get(day) ?? [];
                            const hasData = daySources.length > 0;
                            const isExpanded = expandedDays.has(day);
                            const visible = isExpanded ? daySources : daySources.slice(0, 3);
                            const overflow = daySources.length - 3;
                            return (
                              <div
                                key={day}
                                className={`min-h-[90px] p-1.5 rounded-md border text-left ${hasData ? "border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950" : "border-border bg-background"}`}
                              >
                                <div className={`text-xs font-bold mb-1 ${hasData ? "text-emerald-700 dark:text-emerald-300" : "text-muted-foreground"}`}>{day}</div>
                                {visible.map(s => {
                                  const pulled = isPulledThisMonth(s);
                                  return (
                                    <div key={s.id} className="flex items-start gap-0.5 mb-0.5">
                                      <span className={`mt-[3px] shrink-0 w-1.5 h-1.5 rounded-full ${pulled ? "bg-emerald-500" : "bg-amber-400"}`} title={pulled ? "Pulled this month" : "Not yet pulled"} />
                                      <Link href={`/accounts/${s.account_id}`} className="block text-[10px] leading-tight text-emerald-800 dark:text-emerald-300 hover:underline truncate" title={`${s.accounts?.account_name} — ${s.account_nickname || s.institution_name}`}>
                                        {s.accounts?.account_name || s.account_nickname || s.institution_name}
                                      </Link>
                                    </div>
                                  );
                                })}
                                {!isExpanded && overflow > 0 && (
                                  <button
                                    onClick={() => toggleDay(day)}
                                    className="text-[10px] text-emerald-700 dark:text-emerald-400 hover:underline cursor-pointer"
                                  >
                                    +{overflow} more
                                  </button>
                                )}
                                {isExpanded && daySources.length > 3 && (
                                  <button
                                    onClick={() => toggleDay(day)}
                                    className="text-[10px] text-muted-foreground hover:underline cursor-pointer"
                                  >
                                    show less
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {lateMonth.length > 0 && (
                          <div className="p-3 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950">
                            <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-2">Day 29–31</p>
                            <div className="space-y-1">
                              {lateMonth.map(s => {
                                const pulled = isPulledThisMonth(s);
                                return (
                                  <div key={s.id} className="flex items-center gap-2 text-xs">
                                    <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${pulled ? "bg-emerald-500" : "bg-amber-500"}`} title={pulled ? "Pulled this month" : "Not yet pulled"} />
                                    <span className="text-amber-700 dark:text-amber-400 shrink-0">Day {s.statement_day}</span>
                                    <Link href={`/accounts/${s.account_id}`} className="text-amber-800 dark:text-amber-300 hover:underline truncate">{s.accounts?.account_name || s.institution_name}</Link>
                                    {s.account_nickname && <span className="text-muted-foreground truncate">— {s.account_nickname}</span>}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {sourcesNoDay.length > 0 && (
                          <div className="p-3 rounded-md border">
                            <p className="text-xs font-semibold text-muted-foreground mb-2">No Statement Day Set ({sourcesNoDay.length})</p>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-1">
                              {sourcesNoDay.map(s => (
                                <Link key={s.id} href={`/accounts/${s.account_id}`} className="block text-xs hover:underline truncate text-muted-foreground">{s.accounts?.account_name || s.institution_name}</Link>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()
            ) : sourcesLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredSources.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No financial sources found.</p>
                {(filterAccount || hasActiveFilters) && (
                  <p className="text-sm mt-1">Try adjusting your filters or adding a new source.</p>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead><SortHeader col="institution" label="Institution" /></TableHead>
                      <TableHead><SortHeader col="account" label="Account" /></TableHead>
                      <TableHead><SortHeader col="type" label="Type" /></TableHead>
                      <TableHead><SortHeader col="frequency" label="Frequency" /></TableHead>
                      <TableHead><SortHeader col="status" label="Status" /></TableHead>
                      <TableHead><SortHeader col="last_pulled" label="Last Pulled" /></TableHead>
                      <TableHead><SortHeader col="through_date" label="Through Date" /></TableHead>
                      <TableHead><SortHeader col="assigned" label="Assigned To" /></TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedSources.map((source) => {
                      const statusConfig = STATUS_CONFIG[source.status] || STATUS_CONFIG.ok;
                      const StatusIcon = statusConfig.icon;
                      return (
                        <TableRow key={source.id} data-testid={`row-source-${source.id}`}>
                          <TableCell>
                            <div className="font-medium">{source.institution_name}</div>
                            {source.account_nickname && (
                              <div className="text-sm text-muted-foreground">{source.account_nickname}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {source.accounts?.account_name || "—"}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">
                              {source.source_type.replace("_", " ")}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm capitalize">
                            {source.expected_frequency}
                          </TableCell>
                          <TableCell>
                            <button
                              title="Click to cycle: OK → Attention → Overdue"
                              onClick={() => {
                                const cycle: Record<string, string> = { ok: "attention", attention: "overdue", overdue: "ok" };
                                statusToggleMutation.mutate({ id: source.id, status: cycle[source.status] ?? "ok" });
                              }}
                              className="cursor-pointer"
                            >
                              <Badge variant={statusConfig.variant} className="gap-1 hover:opacity-80 transition-opacity">
                                <StatusIcon className="w-3 h-3" />
                                {statusConfig.label}
                              </Badge>
                            </button>
                          </TableCell>
                          <TableCell className="text-sm">
                            {source.last_pulled_date
                              ? format(new Date(source.last_pulled_date + "T12:00:00"), "MMM d, yyyy")
                              : "Never"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {source.through_date
                              ? format(new Date(source.through_date + "T12:00:00"), "MMM d, yyyy")
                              : "—"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {source.users?.name || "Unassigned"}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenMarkPulled(source)}
                                data-testid={`button-mark-pulled-${source.id}`}
                              >
                                <Calendar className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenHistory(source)}
                                data-testid={`button-history-${source.id}`}
                              >
                                <History className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenEdit(source)}
                                data-testid={`button-edit-${source.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive/60 hover:text-destructive"
                                onClick={() => setDeleteTarget(source)}
                                data-testid={`button-delete-${source.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Source Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Financial Source</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Account *</Label>
              <Select value={addModalAccountId} onValueChange={setAddModalAccountId}>
                <SelectTrigger data-testid="select-modal-account">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>{acc.account_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Institution Name *</Label>
              <Input
                value={formData.institution_name}
                onChange={(e) => setFormData({ ...formData, institution_name: e.target.value })}
                placeholder="e.g., Chase Bank"
                data-testid="input-institution-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Nickname</Label>
              <Input
                value={formData.account_nickname}
                onChange={(e) => setFormData({ ...formData, account_nickname: e.target.value })}
                placeholder="e.g., Operating Account"
                data-testid="input-nickname"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={formData.source_type} onValueChange={(v) => setFormData({ ...formData, source_type: v })}>
                  <SelectTrigger data-testid="select-source-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select value={formData.expected_frequency} onValueChange={(v) => setFormData({ ...formData, expected_frequency: v })}>
                  <SelectTrigger data-testid="select-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>External Hint</Label>
              <Input
                value={formData.external_hint}
                onChange={(e) => setFormData({ ...formData, external_hint: e.target.value })}
                placeholder="e.g., Last 4 digits: 1234"
                data-testid="input-external-hint"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Statement Drop Day</Label>
                <Input
                  type="number"
                  min={1}
                  max={31}
                  value={formData.statement_day}
                  onChange={(e) => setFormData({ ...formData, statement_day: e.target.value ? parseInt(e.target.value) : "" })}
                  placeholder="e.g., 15"
                />
              </div>
              <div className="space-y-2">
                <Label>Login URL</Label>
                <Input
                  value={formData.login_url}
                  onChange={(e) => setFormData({ ...formData, login_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Assigned To</Label>
              <Select value={formData.assigned_user_id} onValueChange={(v) => setFormData({ ...formData, assigned_user_id: v })}>
                <SelectTrigger data-testid="select-assigned-user">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={3}
                data-testid="textarea-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending} data-testid="button-create-source">
              {createMutation.isPending ? "Creating..." : "Create Source"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Source Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Financial Source</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Institution Name *</Label>
              <Input
                value={formData.institution_name}
                onChange={(e) => setFormData({ ...formData, institution_name: e.target.value })}
                placeholder="e.g., Chase Bank"
                data-testid="input-edit-institution-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Nickname</Label>
              <Input
                value={formData.account_nickname}
                onChange={(e) => setFormData({ ...formData, account_nickname: e.target.value })}
                placeholder="e.g., Operating Account"
                data-testid="input-edit-nickname"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={formData.source_type} onValueChange={(v) => setFormData({ ...formData, source_type: v })}>
                  <SelectTrigger data-testid="select-edit-source-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select value={formData.expected_frequency} onValueChange={(v) => setFormData({ ...formData, expected_frequency: v })}>
                  <SelectTrigger data-testid="select-edit-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>External Hint</Label>
              <Input
                value={formData.external_hint}
                onChange={(e) => setFormData({ ...formData, external_hint: e.target.value })}
                placeholder="e.g., Last 4 digits: 1234"
                data-testid="input-edit-external-hint"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Statement Drop Day</Label>
                <Input
                  type="number"
                  min={1}
                  max={31}
                  value={formData.statement_day}
                  onChange={(e) => setFormData({ ...formData, statement_day: e.target.value ? parseInt(e.target.value) : "" })}
                  placeholder="e.g., 15"
                />
              </div>
              <div className="space-y-2">
                <Label>Login URL</Label>
                <Input
                  value={formData.login_url}
                  onChange={(e) => setFormData({ ...formData, login_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Assigned To</Label>
              <Select value={formData.assigned_user_id} onValueChange={(v) => setFormData({ ...formData, assigned_user_id: v })}>
                <SelectTrigger data-testid="select-edit-assigned-user">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={3}
                data-testid="textarea-edit-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending} data-testid="button-update-source">
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Pulled Modal */}
      <Dialog open={showMarkPulledModal} onOpenChange={setShowMarkPulledModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Source as Pulled</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedSource && (
              <div className="p-3 bg-muted rounded-md">
                <p className="font-medium">{selectedSource.institution_name}</p>
                <p className="text-sm text-muted-foreground">{selectedSource.accounts?.account_name}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Pull Date *</Label>
              <Input
                type="date"
                value={markPulledData.pull_date}
                onChange={(e) => setMarkPulledData({ ...markPulledData, pull_date: e.target.value })}
                data-testid="input-pull-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Through Date</Label>
              <Input
                type="date"
                value={markPulledData.through_date}
                onChange={(e) => setMarkPulledData({ ...markPulledData, through_date: e.target.value })}
                data-testid="input-through-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={markPulledData.note}
                onChange={(e) => setMarkPulledData({ ...markPulledData, note: e.target.value })}
                placeholder="Any notes about this pull..."
                rows={2}
                data-testid="textarea-pull-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarkPulledModal(false)}>Cancel</Button>
            <Button onClick={handleMarkPulled} disabled={markPulledMutation.isPending} data-testid="button-confirm-mark-pulled">
              {markPulledMutation.isPending ? "Saving..." : "Mark as Pulled"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Source?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Delete <strong>{deleteTarget?.institution_name}</strong>
            {deleteTarget?.account_nickname ? ` — ${deleteTarget.account_nickname}` : ""}? This will also remove its pull history and cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pull History Sheet */}
      <Sheet open={showHistorySheet} onOpenChange={setShowHistorySheet}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Pull History</SheetTitle>
          </SheetHeader>
          <div className="py-6">
            {selectedSource && (
              <div className="p-3 bg-muted rounded-md mb-6">
                <p className="font-medium">{selectedSource.institution_name}</p>
                <p className="text-sm text-muted-foreground">{selectedSource.accounts?.account_name}</p>
              </div>
            )}
            {pullLog.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>No pull history yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pullLog.map((entry) => (
                  <div key={entry.id} className="p-4 border rounded-lg" data-testid={`pull-log-entry-${entry.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">
                          {format(new Date(entry.pull_date + "T12:00:00"), "MMMM d, yyyy")}
                        </p>
                        {entry.through_date && (
                          <p className="text-sm text-muted-foreground">
                            Through: {format(new Date(entry.through_date + "T12:00:00"), "MMM d, yyyy")}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <User className="w-3 h-3" />
                        {entry.users?.name || "Unknown"}
                      </div>
                    </div>
                    {entry.note && (
                      <p className="text-sm mt-2 text-muted-foreground">{entry.note}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </Layout>
  );
}
